import { useRef, useState } from 'react';
import {
  Button,
  Dropdown,
  Overlay,
  OverlayTrigger,
  Popover,
  Tooltip
} from 'react-bootstrap';
import { BsBoxArrowUpRight, BsChevronDown, BsChevronUp } from 'react-icons/bs';
import { useTranslation } from 'react-i18next';
import useUrlParams from '../../../../hooks/useUrlParams';
import useCsvDownload from '../../../../hooks/useCsvDownload';
import useMeTicketCsvDownload from '../../../../hooks/useMeTicketCsvDownload';
import usePdfDownload from '../../../../hooks/usePdfDownload';
import useLinkClicksCsvDownload from '../../../../hooks/useLinkClicksCsvDownload';
import { useSelector } from 'react-redux';
import requestFromParent from '../../../../utils/requestFromParent';

const ExportReport = ({
  handleShowLoader,
  isFitersActive,
  withParentConfirmation = false,
  isLinkClicksTab = false,
  linkClicks = [],
  isLoading = false
}) => {
  const { t, i18n } = useTranslation();
  const isCompareMode = useSelector((state) => state.filters.isCompareMode);
  const plan = useSelector((state) => state.meTicketData.plan);
  const { isMePage, isGenQrCodePage, isMeTicketPage, isMeShortLinkPage } = useUrlParams();
  const isTicketExportLocked = isMeTicketPage && plan !== 'business';
  const isNoPeriodData = useSelector((state) => state.meTicketData.isNoPeriodData);
  const { downloadPdf, isLoading: isPdfLoading } = usePdfDownload();
  const {
    downloadCsv,
    isExportCsvActive,
    showPopover,
    hidePopover,
    CsvLinkHidden
  } = useCsvDownload({ handleShowLoader });
  const {
    downloadCsv: downloadLinkClicksCsv,
    isExportAvailable: isLinkClicksCsvAvailable,
    CsvLinkHidden: LinkClicksCsvLinkHidden
  } = useLinkClicksCsvDownload(linkClicks);
  const {
    downloadCsv: downloadTicketCsv,
    CsvLinkHidden: TicketCsvLinkHidden
  } = useMeTicketCsvDownload();
  const toggleRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const canDownload = async (fileType) => {
    if (!withParentConfirmation) return true;

    try {
      const response = await requestFromParent('CONFIRM_DOWNLOAD', { fileType });

      return response.allowed; // parent must respond with { allowed: boolean }
    } catch (e) {
      console.error(e.message);

      return false;
    }
  };

  const isLinkClicksCsvExport = isMePage && isLinkClicksTab;
  const isToggleDisabled =
    isLoading ||
    isTicketExportLocked ||
    (!isMeTicketPage &&
      !(isLinkClicksCsvExport ? isLinkClicksCsvAvailable : isExportCsvActive) &&
      (!isCompareMode || isLinkClicksCsvExport)) ||
    (isMeTicketPage && isNoPeriodData && !isCompareMode);

  const toggleClass = `d-flex align-items-center text-decoration-none gap-2 dropdown-toggle-no-caret${
    isMeTicketPage
      ? isToggleDisabled ? ' text-darkgray' : ' text-primary-me-ticket'
      : isMePage
        ? ' text-primary2'
        : isGenQrCodePage
          ? ' text-primary-gen-qr'
          : isMeShortLinkPage ? ' text-primary' : ''
  }${isFitersActive ? ' filter-action' : ''}`;

  return (
    <>
      <CsvLinkHidden />
      <TicketCsvLinkHidden />
      <LinkClicksCsvLinkHidden />

      <OverlayTrigger
        placement="top"
        overlay={
          !isTicketExportLocked && !isCompareMode && (
            (!isMeTicketPage &&
              !(isLinkClicksCsvExport
                ? isLinkClicksCsvAvailable
                : isExportCsvActive)) ||
            (isMeTicketPage && isNoPeriodData)
          ) ? (
            <Tooltip id="tooltip-export" className="opacity-75">
              <span className="fs-7">{t('filters.noCsvData')}</span>
            </Tooltip>
          ) : (
            <span />
          )
        }
      >
        <div
          className={`d-flex align-items-center ${isTicketExportLocked ? ' opacity-75' : ''}`}
        >
          <Dropdown onToggle={setIsOpen}>
            <Dropdown.Toggle
              ref={toggleRef}
              variant="link"
              disabled={isToggleDisabled}
              className={toggleClass}
            >
              <BsBoxArrowUpRight />
              {t('filters.export')}
              {isOpen ? <BsChevronUp /> : <BsChevronDown />}
            </Dropdown.Toggle>

            <Dropdown.Menu className={isMePage ? 'dropdown-menu-me-page' : ''}>
              <Dropdown.Item
                disabled={isCompareMode && !isLinkClicksCsvExport}
                onClick={async (e) => {
                  if (!(await canDownload('csv'))) return;

                  if (isLinkClicksCsvExport) {
                    downloadLinkClicksCsv();
                  } else if (isMeTicketPage) {
                    await downloadTicketCsv();
                  } else {
                    await downloadCsv(e);
                  }
                  window.gtag('event', 'download_statistics', {
                    site_language: i18n.language.toUpperCase()
                  });
                }}
              >
                CSV
              </Dropdown.Item>
              <Dropdown.Item
                disabled={isPdfLoading}
                onClick={async () => {
                  if (!(await canDownload('pdf'))) return;

                  await downloadPdf();
                  window.gtag('event', 'download_statistics', {
                    site_language: i18n.language.toUpperCase()
                  });
                }}
              >
                PDF
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          {isTicketExportLocked && (
            <span className="badge bg-orange text-white p-2 fs-7 fw-normal">
              {t('ticketStatistic.business')}
            </span>
          )}
        </div>
      </OverlayTrigger>

      <Overlay
        show={showPopover.show}
        target={toggleRef.current}
        placement="bottom"
      >
        <Popover id="popover-contained" className="p-3">
          <div className="text-center">{t('filters.interrupt')}</div>
          <div className="d-flex gap-2 mt-2">
            <Button
              variant="outline-dark"
              className="w-50"
              onClick={hidePopover}
            >
              {t('filters.cancel')}
            </Button>
            <Button
              variant={isMePage ? 'primary2' : 'primary'}
              className="w-50"
              onClick={() => {
                hidePopover();
                downloadCsv(null, 1);
              }}
            >
              {t('filters.generate')}
            </Button>
          </div>
        </Popover>
      </Overlay>
    </>
  );
};

export default ExportReport;
